import React, { useMemo, useState, useEffect, useRef } from "react";
import { Box, Stack, Text, Center } from "@mantine/core";

import { useDisplaySchedule } from "@/hooks";
import { useNetwork } from "@/contexts";

import { LoadingOverlay, AppErrorScreen } from "@/components/generic";
import {
  HeaderFooterSection,
  MainSection,
} from "@/components/features/DisplayScreen";

import dayjs from "dayjs";
import { checkContentBlock } from "@/utils";
import { defaultThemeConfig, defaultLayoutConfig } from "@/config";
import { mergeWithDefaults } from "@/components/features/DisplayLayouts/LayoutFormModal/layoutConfigUtils";

export interface DisplayViewProps {
  /** Display ID to load schedule for - required for proper functionality */
  displayId?: string;

  /** Optional date to load schedule for (YYYY-MM-DD format). Defaults to today */
  date?: string;

  /** Additional CSS class name for styling */
  className?: string;

  preview?: boolean;
}

export const DisplayView: React.FC<DisplayViewProps> = ({
  displayId,
  date,
  className,
  preview,
}) => {
  // Get network status from context
  const { isOffline, /*isConnected,*/ lastConnectedAt } = useNetwork();
  const [currentTime, setCurrentTime] = useState(() => dayjs());
  const timeIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Track if we've ever loaded data successfully
  const [hasLoadedData, setHasLoadedData] = useState(false);

  useEffect(() => {
    // Calculate milliseconds until the next minute boundary
    const now = new Date();
    const msUntilNextMinute =
      (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    // Set timeout to align with the next minute boundary
    const alignmentTimeout = setTimeout(() => {
      // Update time immediately when we hit the minute boundary
      setCurrentTime(dayjs());

      // Now start the interval that will fire every minute
      timeIntervalRef.current = setInterval(() => {
        setCurrentTime(dayjs());
      }, 60000);
    }, msUntilNextMinute);

    return () => {
      clearTimeout(alignmentTimeout);
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
        timeIntervalRef.current = null;
      }
    };
  }, []);

  const initialFilter = useMemo(() => {
    return {
      displayId,
      date,
      preview,
    };
  }, [displayId, date, preview]);

  // Use the display schedule hook with the new API
  const { scheduleData, loading, lastError, hasEvents, isFromCache } =
    useDisplaySchedule({
      filter: initialFilter,
      autoLoad: true,
      refreshInterval: isOffline ? 0 : 5 * 60 * 1000, // Disable refresh when offline
    });

  // Track when we successfully load data for the first time
  useEffect(() => {
    if (scheduleData && scheduleData.length > 0 && !hasLoadedData) {
      setHasLoadedData(true);
    }
  }, [scheduleData, hasLoadedData]);

  // Get the first schedule data (should only be one for a single display/date)
  const schedule = scheduleData[0];
  const themeConfig = schedule?.display?.themeConfig ?? defaultThemeConfig;
  const venue = schedule?.display?.venue ?? schedule?.venue;

  // Parse and merge layout configuration
  const layoutConfig = useMemo(() => {
    if (!schedule?.display?.layoutConfig) {
      return defaultLayoutConfig;
    }
    const parsed = schedule.display.layoutConfig;
    return mergeWithDefaults(parsed);
  }, [schedule?.display?.layoutConfig]);

  // Get room name from the first event's room or display info
  const roomName = useMemo(() => {
    if (schedule?.display?.room?.name) {
      return schedule.display.room.name;
    }
    if (schedule?.display?.name) {
      return schedule.display.name;
    }
    return undefined;
  }, [schedule?.display?.room?.name, schedule?.display?.name]);

  // Get display zones (new system)
  const displayZones = useMemo(() => {
    return schedule?.display?.displayZones || [];
  }, [schedule?.display?.displayZones]);

  // Get emergency content block assignments
  const emergencyContentAssignments = useMemo(() => {
    if (!schedule?.display?.emergencyContentBlock) {
      return [];
    }

    if (!schedule?.display?.emergencyContentBlock?.id) {
      return [];
    }

    const localCheckTime = currentTime.local();

    // Convert emergency content block to displayContentAssignment format
    const emergencyBlock = schedule.display.emergencyContentBlock;
    const assignment = {
      id: "",
      contentBlockId: emergencyBlock.id,
      startDatetime: undefined,
      endDatetime: undefined,
      orderNo: 0,
      contentBlock: emergencyBlock,
    };

    if (!checkContentBlock(localCheckTime, assignment)) {
      return [];
    }

    return [assignment];
  }, [schedule?.display?.emergencyContentBlock, currentTime]);

  const hasEmergencyContent = emergencyContentAssignments.length > 0;

  // Check if there's a running message to show
  const hasRunningMessage = useMemo(() => {
    const displayRunningMessages = schedule?.display?.displayRunningMessages;
    if (
      !Array.isArray(displayRunningMessages) ||
      displayRunningMessages.length === 0
    ) {
      return false;
    }

    return true;
  }, [schedule?.display?.displayRunningMessages]);

  // Determine which system we're using
  const isUsingZoneBasedSystem = displayZones.length > 0;

  if (!displayId) {
    return (
      <AppErrorScreen
        title="Display Not Configured"
        message="Please provide a valid display ID to view the schedule."
        emoji="⚙️"
        humorText="Looks like someone forgot to tell us which display to show!"
        showRetryInfo={false}
        className={`${className} display-view display-view--no-display-id`}
      />
    );
  }

  // Let the hook handle cache checking first, only show loading if we're actually loading and have no data
  const showInitialLoading = !hasLoadedData && loading;

  // Only show error if we have an error AND no data from any source (API or cache)
  const shouldShowError = lastError && schedule == null && !loading;

  // Handle the case where we have no data at all (API failed and no cache)
  if (shouldShowError) {
    return (
      <Box className="display-view__no-data" h="100%">
        <Center h="100%">
          <Stack align="center" gap="lg">
            <Text
              size="xl"
              fw="bold"
              c="dimmed"
              className="display-view__no-data-title"
              ta="center"
            >
              {isOffline ? "📡 Offline Mode" : "❌ Unable to Load"}
            </Text>
            <Text
              size="lg"
              c="dimmed"
              className="display-view__no-data-message"
              ta="center"
            >
              {isOffline
                ? "No cached schedule available.\nPlease check your internet connection."
                : "We're having trouble loading the schedule right now."}
            </Text>
            {isOffline && lastConnectedAt && (
              <Text
                size="sm"
                c="dimmed"
                className="display-view__last-connected"
                ta="center"
              >
                Last connected: {lastConnectedAt.toLocaleString()}
              </Text>
            )}
          </Stack>
        </Center>
      </Box>
    );
  }

  // Render initial loading state
  if (showInitialLoading || schedule == null) {
    return <LoadingOverlay visible loadingText="Loading data..." />;
  }

  return (
    <Box
      className={`display-view ${isOffline ? "display-view--offline" : ""} ${
        isFromCache ? "display-view--cached" : ""
      } ${hasEmergencyContent ? "display-view--emergency" : ""} ${
        className || ""
      }`}
      h="100vh"
      style={{
        overflow: "hidden",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
      data-system={isUsingZoneBasedSystem ? "zones" : "legacy"}
      data-has-display-zones={displayZones.length > 0}
      data-has-events={hasEvents}
      data-has-running-message={hasRunningMessage}
      data-has-emergency-content={hasEmergencyContent}
      data-is-offline={isOffline}
      data-has-cached-data={hasLoadedData}
      data-is-from-cache={isFromCache}
    >
      {/* Header - Hidden in emergency mode */}
      {!hasEmergencyContent && layoutConfig.header.height !== "0rem" && (
        <Box className="display-view__header" style={{ flexShrink: 0 }}>
          <HeaderFooterSection
            section={layoutConfig.header}
            sectionType="header"
            venue={venue}
            display={schedule?.display}
            roomName={roomName}
            displayRunningMessages={schedule?.display?.displayRunningMessages}
            themeConfig={themeConfig}
            loading={loading}
            isOffline={isOffline}
          />
        </Box>
      )}

      {/* Main Content Area - Takes all available space */}
      <Box
        className="display-view__main-content"
        style={{
          flex: 1,
          overflow: "hidden",
          ...themeConfig.global,
        }}
      >
        <MainSection
          section={layoutConfig.main}
          scheduleData={schedule}
          displayZones={displayZones}
          emergencyContentAssignments={emergencyContentAssignments}
          roomName={roomName}
          themeConfig={themeConfig}
        />
      </Box>

      {/* Footer - Hidden in emergency mode */}
      {!hasEmergencyContent && layoutConfig.footer.height !== "0rem" && (
        <Box
          className="display-view__footer"
          style={{ flexShrink: 0, width: "100%" }}
        >
          <HeaderFooterSection
            section={layoutConfig.footer}
            sectionType="footer"
            venue={venue}
            display={schedule?.display}
            roomName={roomName}
            displayRunningMessages={schedule?.display?.displayRunningMessages}
            themeConfig={themeConfig}
          />
        </Box>
      )}

      {/* Debug info in development */}
      {/* {process.env.NODE_ENV === "development" && (
        <Box
          className="display-view__debug"
          style={{
            position: "absolute",
            bottom: "var(--mantine-spacing-xs)",
            right: "var(--mantine-spacing-xs)",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            color: "white",
            padding: "var(--mantine-spacing-xs)",
            borderRadius: "var(--mantine-radius-xs)",
            fontSize: "var(--mantine-font-size-xs)",
            fontFamily: "var(--mantine-font-family-monospace)",
            zIndex: 999,
          }}
        >
          System: {isUsingZoneBasedSystem ? "Zones" : "Legacy"} | Layout:{" "}
          {layoutConfig.layoutType} | Header: {layoutConfig.header.height} |
          Footer: {layoutConfig.footer.height} | Zones:{" "}
          {layoutConfig.main.zones.length} | DisplayZones: {displayZones.length}{" "}
          {schedule?.events?.length || 0} | Messages:{" "}
          {hasRunningMessage ? "Yes" : "No"} | Emergency:{" "}
          {hasEmergencyContent ? "Yes" : "No"} | Offline:{" "}
          {isOffline ? "Yes" : "No"} | Connected: {isConnected ? "Yes" : "No"} |
          FromCache: {isFromCache ? "Yes" : "No"}
        </Box>
      )} */}
    </Box>
  );
};
